<template>
  <Teleport to="body">
    <Transition name="consultor-fade">
      <div v-if="modalStore.showConsultor" class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        @click.self="closeConsultorModal">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeConsultorModal" />

        <div
          class="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl overflow-hidden z-10 max-h-[96vh] sm:max-h-[92vh] flex flex-col">

          <!-- Header -->
          <div class="flex-shrink-0 bg-[#004d6b] px-4 py-3 sm:px-6 sm:py-4 flex items-start justify-between gap-2">
            <div class="flex items-start gap-3 min-w-0">
              <div class="min-w-0">
                <h2 class="text-white text-xl font-bold">Falar com um consultor</h2>
                <p class="text-white/65 text-sm mt-0.5">Escolha como prefere ser atendido</p>
              </div>
            </div>
            <button @click="closeConsultorModal"
              class="flex-shrink-0 text-white/60 hover:text-white transition-colors ml-2"
              aria-label="Fechar">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Corpo rolável -->
          <div class="flex-1 min-h-0 overflow-y-auto">
            <!-- Canal de atendimento -->
            <div class="grid grid-cols-3 gap-2 sm:gap-3 px-4 pt-3 pb-2 sm:px-6">
              <button v-for="ch in channels" :key="ch.id" @click="selectedChannel = ch.id"
                class="flex flex-col items-center gap-1 py-2 px-1 sm:py-2.5 sm:px-2 rounded-xl border transition-all text-center"
                :class="selectedChannel === ch.id
                  ? 'border-[#004d6b] bg-[#e6f3f7] border-[1.5px]'
                  : 'border-gray-200 hover:border-[#004d6b]/40 hover:bg-gray-50'">
                <span class="text-[#004d6b] text-xl sm:text-2xl" v-html="ch.icon" />
                <span class="text-xs sm:text-sm font-medium text-gray-800">{{ ch.label }}</span>
                <span class="hidden sm:block text-xs text-gray-400 leading-tight">{{ ch.sub }}</span>
              </button>
            </div>

            <div class="border-t border-gray-100 mx-4 sm:mx-6" />

            <!-- Formulário — altura fixa independente do canal -->
            <div class="px-4 pt-3 pb-2 sm:px-6 flex flex-col gap-2.5">

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-1" for="consultor_nome">Nome *</label>
                  <input id="consultor_nome" v-model="consultorForm.nome" type="text" placeholder="Seu nome"
                    class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/25 focus:border-[#004d6b] transition"
                    :class="consultorErrors.nome ? 'border-red-400' : 'border-gray-300'" />
                  <p v-if="consultorErrors.nome" class="text-red-500 text-xs mt-1">Campo obrigatório</p>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-1" for="consultor_contato">{{ contactLabel }} *</label>
                  <input id="consultor_contato" v-model="consultorForm.contato" :type="contactInputType"
                    :placeholder="contactPlaceholder" @input="selectedChannel !== 'email' && maskPhone($event)"
                    class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/25 focus:border-[#004d6b] transition"
                    :class="consultorErrors.contato ? 'border-red-400' : 'border-gray-300'" />
                  <p v-if="consultorErrors.contato" class="text-red-500 text-xs mt-1">{{ contactErrorMsg }}</p>
                </div>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1" for="consultor_empresa">Empresa</label>
                <input id="consultor_empresa" v-model="consultorForm.empresa" type="text" placeholder="Nome da sua empresa"
                  class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/25 focus:border-[#004d6b] transition" />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-2">Assunto da conversa</label>
                <div class="flex flex-wrap gap-2">
                  <button v-for="tag in assuntos" :key="tag" @click="toggleAssunto(tag)"
                    class="px-3 py-1.5 rounded-full text-xs border transition-all" :class="consultorForm.assuntos.includes(tag)
                      ? 'bg-[#e6f3f7] border-[#004d6b] text-[#004d6b] font-medium'
                      : 'border-gray-200 text-gray-500 hover:border-[#004d6b]/50 hover:text-[#004d6b]'">{{ tag
                      }}</button>
                </div>
              </div>

              <!-- Bloco fixo: horários (WhatsApp/Ligação) ou aviso de prazo (E-mail) -->
              <div class="sm:min-h-[62px]">
                <template v-if="selectedChannel !== 'email'">
                  <label class="block text-xs font-medium text-gray-500 mb-2">Melhor horário para
                    contato</label>
                  <div class="flex flex-wrap gap-2">
                    <button v-for="slot in horarios" :key="slot" @click="consultorForm.horario = slot"
                      class="px-3 py-1.5 rounded-lg text-xs border transition-all" :class="consultorForm.horario === slot
                        ? 'bg-[#004d6b] border-[#004d6b] text-white font-medium'
                        : 'border-gray-200 text-gray-500 hover:border-[#004d6b]/50 hover:text-[#004d6b]'">{{
                          slot }}</button>
                  </div>
                </template>
                <template v-else>
                  <div
                    class="flex items-start gap-2 rounded-lg bg-[#e6f3f7] border border-[#004d6b]/15 px-3 py-2.5">
                    <svg class="w-4 h-4 text-[#004d6b] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="text-xs text-[#004d6b] leading-relaxed">
                      Nossa equipe responde e-mails em até <strong>4 horas</strong> em dias úteis.
                    </p>
                  </div>
                </template>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1" for="consultor_mensagem">Mensagem (opcional)</label>
                <textarea id="consultor_mensagem" v-model="consultorForm.mensagem" rows="4"
                  placeholder="Conte brevemente o que você precisa..."
                  class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#004d6b]/25 focus:border-[#004d6b] transition" />
              </div>

            </div>
          </div>

          <!-- Sucesso -->
          <div v-if="consultorSubmitted"
            class="absolute inset-0 bg-white flex flex-col items-center justify-center text-center px-6 sm:px-8 gap-4 z-20">
            <div class="w-16 h-16 rounded-full bg-[#e6f3f7] flex items-center justify-center">
              <svg class="w-8 h-8 text-[#004d6b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-gray-800">Solicitação enviada!</h3>
              <p class="text-sm text-gray-500 mt-1 leading-relaxed">
                Um consultor da Kali Sistemas entrará em contato com você
                <span v-if="selectedChannel !== 'email'"> no horário escolhido</span>
                <span v-else> em até 4 horas</span>.
              </p>
            </div>
            <button @click="closeConsultorModal"
              class="px-6 py-2 bg-[#004d6b] text-white text-sm rounded-lg hover:bg-[#003d55] transition font-medium">
              Fechar
            </button>
          </div>

          <!-- Footer -->
          <div v-if="!consultorSubmitted"
            class="flex-shrink-0 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 px-4 py-3 sm:px-6 border-t border-gray-100 bg-gray-50">
            <p class="text-xs text-gray-400 flex items-center gap-1">
              <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Seus dados estão seguros
            </p>
            <div class="flex gap-2">
              <button @click="closeConsultorModal"
                class="flex-1 sm:flex-initial px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-500 hover:bg-white transition">
                Cancelar
              </button>
              <button @click="submitConsultor" :disabled="consultorSubmitting"
                class="flex-1 sm:flex-initial justify-center px-5 py-2 text-sm bg-[#004d6b] text-white rounded-lg hover:bg-[#003d55] transition font-medium flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed">
                <svg v-if="consultorSubmitting" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 12a8 8 0 018-8V4" />
                </svg>
                {{ consultorSubmitting ? "Enviando..." : "Enviar" }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import api from "@/services/api";
import { pushEvent } from "@/composables/useGtm";
import { usePhoneMask } from "@/composables/usePhoneMask";
import { useModalStore } from "@/stores/useModalStore";

defineOptions({ name: "ConsultorModal" });

const modalStore = useModalStore();

const consultorSubmitted = ref(false);
const selectedChannel = ref("whatsapp");

const channels = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    sub: "Resposta imediata",
    icon: `<svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M5.84 20.93l.553-2.019A8.96 8.96 0 013 12C3 7.03 7.03 3 12 3s9 4.03 9 9-4.03 9-9 9a8.96 8.96 0 01-4.733-1.343l-2.427.273z"/></svg>`,
  },
  {
    id: "ligacao",
    label: "Ligação",
    sub: "Consultor liga pra você",
    icon: `<svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>`,
  },
  {
    id: "email",
    label: "E-mail",
    sub: "Retorno em até 4h",
    icon: `<svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>`,
  },
];

const assuntos = [
  "Conhecer os planos",
  "Dúvida técnica",
  "Migração de sistema",
  "Preços e negociação",
  "Outro",
];

const horarios = ["Manhã (8h–12h)", "Tarde (13h–18h)", "Qualquer horário"];

const consultorForm = ref({
  nome: "",
  contato: "",
  empresa: "",
  assuntos: ["Conhecer os planos"],
  horario: "Manhã (8h–12h)",
  mensagem: "",
});

const consultorErrors = ref({ nome: false, contato: false });

// Campos dinâmicos conforme canal
const contactLabel = computed(() => {
  if (selectedChannel.value === "email") return "E-mail";
  if (selectedChannel.value === "ligacao") return "Telefone";
  return "WhatsApp";
});

const contactInputType = computed(() =>
  selectedChannel.value === "email" ? "email" : "tel"
);

const contactPlaceholder = computed(() => {
  if (selectedChannel.value === "email") return "Seu e-mail";
  if (selectedChannel.value === "ligacao") return "Seu telefone";
  return "Seu WhatsApp";
});

const contactErrorMsg = computed(() =>
  selectedChannel.value === "email" ? "E-mail inválido" : "Campo obrigatório"
);

const toggleAssunto = (tag) => {
  const idx = consultorForm.value.assuntos.indexOf(tag);
  if (idx === -1) consultorForm.value.assuntos.push(tag);
  else consultorForm.value.assuntos.splice(idx, 1);
};

const { formatPhone } = usePhoneMask();
const maskPhone = (e) => {
  consultorForm.value.contato = formatPhone(e.target.value);
};

const consultorSubmitting = ref(false);

const submitConsultor = async () => {
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(consultorForm.value.contato);
  consultorErrors.value.nome = !consultorForm.value.nome.trim();
  consultorErrors.value.contato =
    selectedChannel.value === "email"
      ? !emailOk
      : !consultorForm.value.contato.trim();
  if (consultorErrors.value.nome || consultorErrors.value.contato) return;

  const isEmail = selectedChannel.value === "email";
  const mensagemFinal = [
    consultorForm.value.assuntos.length ? `Assunto(s): ${consultorForm.value.assuntos.join(", ")}` : "",
    consultorForm.value.horario ? `Horário preferido: ${consultorForm.value.horario}` : "",
    consultorForm.value.mensagem || "",
  ].filter(Boolean).join("\n");

  const payload = {
    name: consultorForm.value.nome.trim(),
    email: isEmail ? consultorForm.value.contato.trim() : null,
    phone: !isEmail ? consultorForm.value.contato.trim() : null,
    company: consultorForm.value.empresa.trim() || null,
    message: mensagemFinal || null,
    source: "consultor",
    page_url: window.location.href,
    payload: {
      source_cta: "consultor-modal",
      canal: selectedChannel.value,
      assuntos: consultorForm.value.assuntos,
      horario: consultorForm.value.horario,
    },
  };

  try {
    consultorSubmitting.value = true;
    await api.post("/public/lead", payload);
    consultorSubmitted.value = true;
    pushEvent("lead_consultor", { source_page: window.location.pathname, canal: selectedChannel.value });
  } catch {
    // mantém o modal aberto para o usuário tentar novamente
  } finally {
    consultorSubmitting.value = false;
  }
};

const closeConsultorModal = () => {
  modalStore.closeConsultor();
  setTimeout(() => {
    consultorSubmitted.value = false;
    selectedChannel.value = "whatsapp";
    consultorForm.value = {
      nome: "", contato: "", empresa: "",
      assuntos: ["Conhecer os planos"],
      horario: "Manhã (8h–12h)",
      mensagem: "",
    };
    consultorErrors.value = { nome: false, contato: false };
  }, 300);
};

const handleConsultorEsc = (e) => {
  if (e.key !== "Escape") return;
  if (!modalStore.showConsultor) return;
  closeConsultorModal();
};

watch(() => modalStore.showConsultor, (open) => {
  if (open) window.addEventListener("keydown", handleConsultorEsc);
  else window.removeEventListener("keydown", handleConsultorEsc);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleConsultorEsc);
});
</script>

<style scoped>
.consultor-fade-enter-active,
.consultor-fade-leave-active {
  transition: opacity 200ms ease;
}

.consultor-fade-enter-active .relative.bg-white,
.consultor-fade-leave-active .relative.bg-white {
  transition: transform 200ms ease, opacity 200ms ease;
}

.consultor-fade-enter-from,
.consultor-fade-leave-to {
  opacity: 0;
}

.consultor-fade-enter-from .relative.bg-white {
  transform: scale(0.95) translateY(-8px);
}
</style>
